/** Template TEXT FACTS: the numbers a template's player-facing text states
 * (its pitch and disclosure, which become a composed game's design summary
 * and rules) bound to the game config they describe, so an adapted or tuned
 * game's text states ITS numbers, never the template's ("serve ten
 * customers" for a game whose objective is twelve).
 *
 * A released template is immutable (retired.ts), so the bindings live here,
 * beside it, as platform data: each names an exact phrase of the template's
 * text, the number token inside it, and where that number comes from. The
 * text is rendered from the game, never from the template: an unmodified
 * template renders byte-identical text (test/compose/textFacts.test.ts pins
 * it), and a game whose structure no longer has a stated fact cannot render
 * its rules at all — an adaptation check surfaces that as repair feedback.
 * A generated template has no bindings: its text was written for its game. */
import type { JsonValue } from '../../json.js';
import type { GameSpecV1 } from '../spec.js';
import { readPath, type TemplateDisclosureV1, type TemplateOverridesV1, type TemplateRulesTextV1, type TemplateV1 } from './template.js';

/** Where a stated number comes from, in the TEMPLATE's kinds (renames apply):
 *  - `path`: a module-config value (`/archetypes/<kind>/modules/<id>/…`, `/globals/<key>/…`);
 *  - `length`: the length of a module-config array (a ticket's courses);
 *  - `every`: the spacing of the entries of a phase list matching `spawns`
 *    (a phase that spawns that kind) or `phase` (a phase of that kind) —
 *    "every third customer is a VIP"; entries at irregular spacing state no
 *    such fact. */
export type TextFactSourceV1 =
  | { path: string }
  | { length: string }
  | { every: string; spawns: string }
  | { every: string; phase: string };

export interface TextFactV1 {
  /** The exact phrase of the template text that states the fact; every
   * occurrence across the pitch and the disclosure is rendered. */
  phrase: string;
  /** The number as the phrase writes it (a whole word inside `phrase`). */
  token: string;
  source: TextFactSourceV1;
  /** The stated number is `value * scale + offset` (ticks → seconds: 1/30). */
  scale?: number;
  offset?: number;
  format: 'cardinal' | 'digits' | 'ordinal' | 'percent' | 'times';
  /** The whole phrase when the stated number is one (singular agreement). */
  one?: string;
}

const TICKS_PER_SECOND = 30;
const SECOND = 1 / TICKS_PER_SECOND;

const objectiveCount = { path: '/globals/flow.objective/count' } as const;

/** Every maintained template's stated config numbers. A number the text
 * states that no tunable or adaptation can change (two corner pipes) is not
 * a fact; test/compose/textFacts.test.ts fails any unbound statement of a
 * tunable's value. */
export const TEMPLATE_TEXT_FACTS: Readonly<Record<string, readonly TextFactV1[]>> = Object.freeze({
  'bump-arcade/sewer-1983': [
    { phrase: 'Clear six phases', token: 'six', source: objectiveCount, format: 'cardinal', one: 'Clear one phase' },
    { phrase: 'through 22 patterns', token: '22', source: { length: '/globals/flow.director/phases' }, format: 'cardinal', one: 'through one pattern' },
  ],
  'formation-shooter/stolen-wings': [
    { phrase: 'clears five waves', token: 'five', source: objectiveCount, format: 'cardinal', one: 'clears one wave' },
    { phrase: 'Clear wave 5', token: '5', source: objectiveCount, format: 'digits' },
    { phrase: 'all three fighters', token: 'three', source: { path: '/archetypes/star-fighter/modules/core.vitals/lives' }, format: 'cardinal', one: 'your one fighter' },
    { phrase: 'take two hits each', token: 'two', source: { path: '/archetypes/boss-guardian/modules/rule.destructible/hp' }, format: 'cardinal', one: 'take one hit each' },
    { phrase: 'Every third wave', token: 'third', source: { every: '/globals/flow.director/phases', phase: 'bonus' }, format: 'ordinal' },
  ],
  'mine-platformer/bounty-bob': [
    { phrase: 'Six bays win', token: 'Six', source: objectiveCount, format: 'cardinal', one: 'One bay wins' },
    { phrase: 'all six bays', token: 'six', source: objectiveCount, format: 'cardinal', one: 'the one bay' },
    { phrase: 'Falling two tiers', token: 'two', source: { path: '/archetypes/bounty-bob/modules/rule.fallDamage/dazeAt' }, format: 'cardinal', one: 'Falling one tier' },
    { phrase: 'more than three costs', token: 'three', source: { path: '/archetypes/bounty-bob/modules/rule.fallDamage/killAt' }, offset: -1, format: 'cardinal' },
  ],
  'service-counter/scoop-rush': [
    { phrase: 'ten customers', token: 'ten', source: objectiveCount, format: 'cardinal', one: 'one customer' },
    { phrase: 'Every third customer', token: 'third', source: { every: '/globals/flow.director/phases', spawns: 'vip-customer' }, format: 'ordinal' },
    { phrase: '70%', token: '70', source: { path: '/globals/flow.grade/minRatio' }, scale: 100, format: 'percent' },
    { phrase: 'three ruined orders', token: 'three', source: { path: '/archetypes/server/modules/core.vitals/lives' }, format: 'cardinal', one: 'one ruined order' },
  ],
  'service-counter/glow-rush': [
    { phrase: 'five happy clients', token: 'five', source: objectiveCount, format: 'cardinal', one: 'one happy client' },
    { phrase: 'Serve five clients', token: 'five', source: objectiveCount, format: 'cardinal', one: 'Serve one client' },
    { phrase: 'four-step', token: 'four', source: { length: '/archetypes/client/modules/rule.ticket/courses' }, format: 'cardinal' },
    { phrase: 'four exact steps', token: 'four', source: { length: '/archetypes/client/modules/rule.ticket/courses' }, format: 'cardinal', one: 'one exact step' },
    { phrase: 'all four steps', token: 'four', source: { length: '/archetypes/client/modules/rule.ticket/courses' }, format: 'cardinal', one: 'the one step' },
  ],
  'strike-flight/blue-max': [
    { phrase: 'Twelve bombs', token: 'Twelve', source: { path: '/globals/bombs/capacity' }, format: 'cardinal', one: 'One bomb' },
    { phrase: 'five hull points', token: 'five', source: { path: '/archetypes/strike-plane/modules/core.vitals/lives' }, format: 'cardinal', one: 'one hull point' },
    { phrase: 'smokes at three', token: 'three', source: { path: '/archetypes/strike-plane/modules/fx.condition/stages/0/atOrBelow' }, format: 'cardinal' },
    { phrase: 'steering slower, at one', token: 'one', source: { path: '/archetypes/strike-plane/modules/fx.condition/stages/1/atOrBelow' }, format: 'cardinal' },
  ],
  'lander/tranquility-nine': [
    { phrase: 'bank three landings', token: 'three', source: objectiveCount, format: 'cardinal', one: 'bank one landing' },
    { phrase: 'Bank three clean pad landings', token: 'three', source: objectiveCount, format: 'cardinal', one: 'Bank one clean pad landing' },
    { phrase: 'one of two lives', token: 'two', source: { path: '/archetypes/descent-module/modules/core.vitals/lives' }, format: 'cardinal', one: 'your one life' },
    { phrase: 'a second crash', token: 'second', source: { path: '/archetypes/descent-module/modules/core.vitals/lives' }, format: 'ordinal' },
    { phrase: '15% narrower', token: '15', source: { path: '/globals/flow.escalation/curves/0/rate' }, scale: -100, offset: 100, format: 'percent' },
  ],
  'downhill/ski-2600': [
    { phrase: 'ninth wipeout', token: 'ninth', source: { path: '/archetypes/skier/modules/core.vitals/lives' }, format: 'ordinal' },
    { phrase: 'adds two seconds', token: 'two', source: { path: '/globals/rule.course/penaltyTicks' }, scale: SECOND, format: 'cardinal', one: 'adds one second' },
    { phrase: 'plus two seconds per', token: 'two', source: { path: '/globals/rule.course/penaltyTicks' }, scale: SECOND, format: 'cardinal', one: 'plus one second per' },
    { phrase: 'missing six of', token: 'six', source: { path: '/globals/rule.course/disqualifyAfter' }, format: 'cardinal' },
    { phrase: 'Missing six gates', token: 'six', source: { path: '/globals/rule.course/disqualifyAfter' }, format: 'cardinal', one: 'Missing one gate' },
  ],
  'tank-arena/arena-tanks': [
    { phrase: 'first to three', token: 'three', source: objectiveCount, format: 'cardinal' },
    { phrase: 'win three rounds', token: 'three', source: objectiveCount, format: 'cardinal', one: 'win one round' },
    { phrase: 'Best of five', token: 'five', source: objectiveCount, scale: 2, offset: -1, format: 'cardinal' },
    { phrase: 'at most three of yours', token: 'three', source: { path: '/archetypes/tank/modules/weapon.cannon/maxAlive' }, format: 'cardinal' },
    { phrase: 'arena wall once', token: 'once', source: { path: '/archetypes/shell/modules/body.ricochet/bounces' }, format: 'times' },
    { phrase: 'after six seconds', token: 'six', source: { path: '/archetypes/shell/modules/body.ricochet/lifeTicks' }, scale: SECOND, format: 'cardinal', one: 'after one second' },
    { phrase: 'one-minute round clock', token: 'one', source: { path: '/globals/flow.rounds/roundTicks' }, scale: SECOND / 60, format: 'cardinal' },
    { phrase: 'after ten minutes', token: 'ten', source: { path: '/globals/flow.rounds/matchTicks' }, scale: SECOND / 60, format: 'cardinal', one: 'after one minute' },
  ],
});

const CARDINALS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
  'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
const ORDINALS = ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
  'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth', 'twentieth'];

/** A number as plain digits: at most two decimals, no float noise. */
const digits = (n: number): string => String(Math.round(n * 100) / 100);

/** `n` written as a fact's format writes it (lower case). */
export function formatTextFactNumber(n: number, format: TextFactV1['format']): string {
  const whole = Number.isInteger(n) && n >= 0;
  switch (format) {
    case 'cardinal': return whole && n < CARDINALS.length ? CARDINALS[n]! : digits(n);
    case 'digits': case 'percent': return digits(n);
    case 'ordinal': {
      if (whole && n < ORDINALS.length) return ORDINALS[n]!;
      const r = Math.round(n), tens = r % 100;
      return `${r}${tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][r % 10] ?? 'th'}`;
    }
    case 'times': return n === 1 ? 'once' : n === 2 ? 'twice' : `${formatTextFactNumber(n, 'cardinal')} times`;
  }
}

/** Template kinds → the game's (a rename moves every path's kind). */
function renamedPath(path: string, rename: Readonly<Record<string, string>>): string {
  const parts = path.split('/');
  if (parts[1] === 'archetypes' && parts[2] !== undefined && rename[parts[2]]) parts[2] = rename[parts[2]]!;
  return parts.join('/');
}

function read(game: GameSpecV1, path: string): JsonValue | undefined {
  try { return readPath(game, path); } catch { return undefined; }
}

/** The fact's raw value in `game`, or why the game no longer has it. */
function factValue(game: GameSpecV1, source: TextFactSourceV1, rename: Readonly<Record<string, string>>): number | string {
  if ('path' in source) {
    const value = read(game, renamedPath(source.path, rename));
    return typeof value === 'number' ? value : `${source.path} is not a number in this game`;
  }
  if ('length' in source) {
    const value = read(game, renamedPath(source.length, rename));
    return Array.isArray(value) ? value.length : `${source.length} is not a list in this game`;
  }
  const list = read(game, renamedPath(source.every, rename));
  if (!Array.isArray(list)) return `${source.every} is not a list in this game`;
  const wanted = 'spawns' in source ? rename[source.spawns] ?? source.spawns : source.phase;
  const hits = list.flatMap((entry, i) => {
    const phase = entry as { kind?: unknown; spawns?: Array<{ archetype?: unknown }> };
    const match = 'spawns' in source ? (phase.spawns ?? []).some(s => s.archetype === wanted) : phase.kind === wanted;
    return match ? [i] : [];
  });
  const what = 'spawns' in source ? `phases spawning ${wanted}` : `${wanted} phases`;
  if (!hits.length) return `${source.every} has no ${what}`;
  // Regular spacing p (from the gaps, or a lone entry's position) that runs
  // the whole list: the first within p of the start, the next past the end.
  const period = hits.length > 1 ? hits[1]! - hits[0]! : hits[0]! + 1;
  const regular = period > 1 && hits.every((h, i) => i === 0 || h - hits[i - 1]! === period)
    && hits[0]! < period && hits[hits.length - 1]! + period >= list.length;
  return regular ? period : `${source.every} has ${what} at irregular positions (${hits.map(h => h + 1).join(', ')} of ${list.length})`;
}

function capitalizeLike(word: string, like: string): string {
  return like[0] === like[0]!.toUpperCase() && like[0] !== like[0]!.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** One fact's phrase as `game` states it, or why it cannot be stated. */
export function renderTextFact(fact: TextFactV1, game: GameSpecV1, rename: Readonly<Record<string, string>>): { ok: true; text: string } | { ok: false; error: string } {
  const value = factValue(game, fact.source, rename);
  if (typeof value === 'string') return { ok: false, error: `"${fact.phrase}": ${value}` };
  const n = Math.round((value * (fact.scale ?? 1) + (fact.offset ?? 0)) * 100) / 100;
  if (n === 1 && fact.one !== undefined) return { ok: true, text: fact.one };
  const word = capitalizeLike(formatTextFactNumber(n, fact.format), fact.token);
  const at = fact.phrase.search(new RegExp(`(?<![\\w-])${fact.token}(?![\\w])`));
  return { ok: true, text: fact.phrase.slice(0, at) + word + fact.phrase.slice(at + fact.token.length) };
}

export interface TemplateTextV1 { pitch: string; disclosure: TemplateDisclosureV1 }

export type TemplateTextResultV1 = { ok: true; text: TemplateTextV1 } | { ok: false; errors: string[] };

/** A template's pitch and disclosure as they hold for `game` (the template's
 * game after its patch; `rename` = the patch's kind renames): every bound
 * phrase states the game's own number. Fails when the game no longer has a
 * fact its rules state. */
export function templateTextFor(template: TemplateV1, game: GameSpecV1, rename: Readonly<Record<string, string>>): TemplateTextResultV1 {
  const facts = template.id in TEMPLATE_TEXT_FACTS ? TEMPLATE_TEXT_FACTS[template.id]! : [];
  const errors: string[] = [];
  const stated = new Map<string, string>();
  for (const fact of facts) {
    const rendered = renderTextFact(fact, game, rename);
    if (rendered.ok) stated.set(fact.phrase, rendered.text); else errors.push(rendered.error);
  }
  if (errors.length) return { ok: false, errors };
  // One pass over the template's own text: a stated phrase is never re-read as another's.
  const phrases = new RegExp([...stated.keys()].sort((a, b) => b.length - a.length).map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'g');
  const render = (text: string) => stated.size ? text.replace(phrases, phrase => stated.get(phrase)!) : text;
  const d = template.disclosure;
  return { ok: true, text: { pitch: render(template.pitch), disclosure: {
    coreVerbs: [...d.coreVerbs], mechanics: d.mechanics.map(render), controls: render(d.controls), winCondition: render(d.winCondition),
  } } };
}

// ── A game's own rules text (patch.rules) ────────────────────────────────────

/** True when a patch makes a game the template's text no longer describes:
 * it renames a kind, rewrites a kind's role or a placed item's label, or
 * changes the structure. Such a game states its own rules (patch.rules);
 * tunables, palette, summary and background alone keep the template's text
 * (its numbers restated by {@link templateTextFor}). */
export function rulesTextRequired(patch: TemplateOverridesV1): boolean {
  return !!(patch.structure || Object.keys(patch.rename ?? {}).length
    || Object.keys(patch.content ?? {}).some(path => /^\/archetypes\/[^/]+\/role$/.test(path) || /\/label$/.test(path)));
}

const NOUN_KEYS = new Set(['id', 'item', 'options']);
const IDENT = /^[a-z][a-z0-9-]{2,39}$/;

/** The things a game is made of, as nouns: its kinds, and the ids its
 * modules name (a ticket's courses and their options, a placed item). */
export function gameNouns(game: GameSpecV1): Set<string> {
  const nouns = new Set(Object.keys(game.archetypes));
  const walk = (node: JsonValue | undefined, key: string): void => {
    if (typeof node === 'string') { if (NOUN_KEYS.has(key) && IDENT.test(node)) nouns.add(node); return; }
    if (Array.isArray(node)) { for (const item of node) walk(item, key); return; }
    if (node && typeof node === 'object') for (const [k, v] of Object.entries(node)) walk(v, k);
  };
  for (const archetype of Object.values(game.archetypes)) for (const use of archetype.modules) walk(use.config as JsonValue, '');
  for (const use of game.globals) walk(use.config as JsonValue, '');
  return nouns;
}

const words = (noun: string) => noun.split('-');
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A word as text may spell it: plural or not, and an "-or" id in either
 * spelling ("flavor" / "flavour"). */
const spelled = (word: string) => `${escape(word).replace(/or$/, 'ou?r')}(?:s|es)?`;
/** `noun` (kebab-case) written in text: its words, any separator. */
const nounPattern = (noun: string) => new RegExp(`(?<![\\w-])${words(noun).map(escape).slice(0, -1).concat(spelled(words(noun).at(-1)!)).join('[\\s-]')}(?![\\w])`, 'i');

/** A template noun the game no longer has as written: the whole noun and,
 * for a compound, its head word when none of the game's nouns uses it
 * ("mutant organism" and "organism"; never "customer" while a
 * `beach-customer` remains). */
function staleForms(noun: string, gameWords: ReadonlySet<string>): string[] {
  const head = words(noun).at(-1)!;
  return words(noun).length > 1 && !gameWords.has(head) ? [noun, head] : [noun];
}

/** Checks 2 and 3 of {@link rulesTextProblems}: the template nouns `text`
 * names that the game removed, or renamed to a name that drops them. */
function nounProblems(template: TemplateV1, game: GameSpecV1, rename: Readonly<Record<string, string>>, text: string): string[] {
  const has = gameNouns(game);
  const gameWords = new Set([...has].flatMap(words));
  const problems = new Set<string>();
  const renamed = new Map(Object.entries(rename));
  for (const noun of gameNouns(template.spec)) {
    if (has.has(noun)) continue;
    const to = renamed.get(noun);
    if (to !== undefined && words(noun).every(w => words(to).includes(w))) continue;
    for (const form of staleForms(noun, gameWords)) {
      if (!nounPattern(form).test(text)) continue;
      problems.add(to === undefined ? `the rules name "${form.replace(/-/g, ' ')}", which this game no longer has`
        : `the rules name "${form.replace(/-/g, ' ')}", which this game calls "${to.replace(/-/g, ' ')}"`);
    }
  }
  return [...problems];
}

/** How a stated number may be written: the fact's own format, cardinal
 * and digits, and the words for one and two. */
function numberForms(n: number, format: TextFactV1['format']): string[] {
  const forms = new Set([formatTextFactNumber(n, format), formatTextFactNumber(n, 'cardinal'), formatTextFactNumber(n, 'digits')]);
  if (n === 1) for (const w of ['one', 'single', 'once', 'a', 'an']) forms.add(w);
  if (n === 2) forms.add('twice');
  return [...forms];
}

/** Every way `rules` misdescribes `game` (empty when it describes it):
 *  1. a number the template states whose source the game still has, not
 *     stated with the game's value (a fact whose source the game no longer
 *     has is not required: an honest adaptation removed it);
 *  2. a noun of a kind, course or item the game no longer has;
 *  3. a template name of a renamed kind the new name does not keep. */
export function rulesTextProblems(template: TemplateV1, game: GameSpecV1, rename: Readonly<Record<string, string>>, rules: TemplateRulesTextV1): string[] {
  const text = [rules.pitch, ...rules.mechanics, rules.controls, rules.winCondition].join('\n');
  const problems: string[] = [];
  const checked = new Set<string>();
  for (const fact of TEMPLATE_TEXT_FACTS[template.id] ?? []) {
    const value = factValue(game, fact.source, rename);
    if (typeof value === 'string') continue;
    const n = Math.round((value * (fact.scale ?? 1) + (fact.offset ?? 0)) * 100) / 100;
    const key = `${JSON.stringify(fact.source)}=${n}`;
    if (checked.has(key)) continue;
    checked.add(key);
    const forms = numberForms(n, fact.format);
    if (!forms.some(form => new RegExp(`(?<![\\w.])${escape(form)}(?![\\w])`, 'i').test(text))) {
      const rendered = renderTextFact(fact, game, rename);
      problems.push(`the rules must state the game's number for the template's "${fact.phrase}" (here: "${rendered.ok ? rendered.text : forms[0]}")`);
    }
  }
  return [...problems, ...nounProblems(template, game, rename, text)];
}

/** THE player-facing text of a composed game (the template's game under
 * `patch`): its own rules when the patch carries them, checked against the
 * game; otherwise the template's text with the game's numbers. Fails when
 * the text would misdescribe the game. */
export function gameTextFor(template: TemplateV1, game: GameSpecV1, patch: TemplateOverridesV1): TemplateTextResultV1 {
  const rename = patch.rename ?? {};
  if (!patch.rules) return templateTextFor(template, game, rename);
  const errors = rulesTextProblems(template, game, rename, patch.rules);
  if (errors.length) return { ok: false, errors };
  const { pitch, mechanics, controls, winCondition } = patch.rules;
  return { ok: true, text: { pitch, disclosure: { coreVerbs: [...template.disclosure.coreVerbs], mechanics: [...mechanics], controls, winCondition } } };
}
